/**
 * Utility for detecting and redacting sensitive personally identifiable information (PII)
 * from user input.
 *
 * ## Security Controls Compliance
 *
 * This implementation is the TypeScript port of the Kotlin reference implementation
 * for the following L3 security controls:
 *
 * | Control ID | Version | Requirement |
 * |------------|---------|-------------|
 * | MOB_0001 | 1.0.0 | Client-Side PII Detection and Redaction |
 * | MOB_0002 | 1.0.0 | Sensitive Data Pattern Configuration |
 * | MOB_0003 | 1.0.0 | PII Redaction in User Input Flows |
 *
 * These L3 controls implement the following L2 control requirements:
 *
 * | Control ID | Version | Requirement |
 * |------------|---------|-------------|
 * | APP_0187 | 1.0.0 | Sensitive data is excluded from application logs via data masking/redaction |
 * | BR_0007 | 1.0.0 | Apply redaction rules for sensitive data types (PII, PCI, PHI) |
 * | IOT_0083 | 1.0.0 | Redact PII in error messages, implement data minimization |
 *
 * ## Supported Sensitive Data Types
 *
 * **High Confidence (pattern-only detection):**
 * - Social Security Numbers (SSN)
 * - Credit Card Numbers (Visa, Mastercard, Amex, Discover, etc.)
 * - Email Addresses
 * - Phone Numbers (US and International)
 * - Individual Taxpayer Identification Numbers (ITIN)
 *
 * **Keyword-Required (context-sensitive detection):**
 * - US Passport Numbers
 * - Date of Birth
 * - Marriott Bonvoy Loyalty Numbers
 * - US Driver's License Numbers
 * - Bank Account Numbers
 *
 * ## Thread-Safety Note
 *
 * All RegExp patterns are stored as `{ source, flags }` descriptors rather than live
 * RegExp literals.  `redact()` constructs a fresh `new RegExp(source, flags)` for every
 * invocation, so the mutable `lastIndex` field is never shared across callers — safe
 * under concurrent React renders and async usage (MOB_0002 v1.0.0 compliance).
 */

const DIGITS_TO_SHOW = 4;
const PROXIMITY_WINDOW = 50;

/**
 * Enumeration of sensitive data types that can be detected and redacted.
 */
export enum SensitiveDataType {
  SSN = 'SSN',
  CREDIT_CARD = 'CREDIT_CARD',
  EMAIL = 'EMAIL',
  PHONE_US = 'PHONE_US',
  PHONE_INTL = 'PHONE_INTL',
  PASSPORT_US = 'PASSPORT_US',
  DATE_OF_BIRTH = 'DATE_OF_BIRTH',
  LOYALTY_NUMBER = 'LOYALTY_NUMBER',
  DRIVERS_LICENSE = 'DRIVERS_LICENSE',
  BANK_ACCOUNT = 'BANK_ACCOUNT',
  ITIN = 'ITIN',
}

/**
 * Represents the result of a redaction operation.
 */
export interface RedactionResult {
  /** The input text with all sensitive data redacted */
  redactedText: string;
  /** True if any sensitive data was detected and redacted */
  containsSensitiveData: boolean;
  /** Set of sensitive data types that were detected in the input */
  detectedTypes: Set<SensitiveDataType>;
}

/**
 * Immutable descriptor for a single regex pattern.
 * Stored as source + flags so redact() can clone a fresh RegExp each call,
 * avoiding shared mutable lastIndex state (CWE-1333 / thread-safety).
 */
interface PatternDescriptor {
  source: string;
  flags: string;
}

/**
 * Pattern configuration for sensitive data detection.
 */
interface PatternConfig {
  /** The type of sensitive data this pattern detects */
  type: SensitiveDataType;
  /** Immutable pattern descriptors — cloned into fresh RegExp instances inside redact() */
  patterns: PatternDescriptor[];
  /** Function to transform matched text into redacted form */
  redactor: (match: string) => string;
  /** Whether a proximity keyword is required for detection */
  requiresKeyword: boolean;
  /** List of keywords that must be near the match (if requiresKeyword is true) */
  keywords: string[];
}

// ==================== SSN Patterns ====================
/**
 * US Social Security Number patterns.
 * Formats: XXX-XX-XXXX, XXX XX XXXX, XXXXXXXXX
 * Note: First digit cannot be 9 (reserved for ITIN)
 */
const ssnPatterns: PatternDescriptor[] = [
  { source: '\\b[0-8]\\d{2}[\\s-]\\d{2}[\\s-]\\d{4}\\b', flags: 'g' }, // With separators
  { source: '\\b[0-8]\\d{8}\\b', flags: 'g' }, // Without separators
];

// ==================== Credit Card Patterns ====================
/**
 * Credit card number patterns for major card networks.
 *
 * Network-specific patterns (highest confidence, no separator requirement):
 *   - Visa:        4XXX …  (16 digits, or 19 digits for extended format)
 *   - Mastercard:  51-55XX / 2221-2720 (16 digits)
 *   - Amex:        34XX / 37XX (15 digits, 4-6-5 groups)
 *   - Discover:    6011 / 644-649 / 65XX (16 digits)
 *   - Diners Club: 36XX / 38XX / 300-305 (14 digits) - WEB-193653
 *
 * Generic formatted fallback (requires mandatory separator to avoid false positives
 * on long numeric IDs — replaces the removed catch-all \b\d{15,16}\b):
 *   - 16-digit with separators: XXXX[- /]XXXX[- /]XXXX[- /]XXXX
 *   - 15-digit with separators: XXXX[- /]XXXXXX[- /]XXXXX  (Amex-style)
 *   - 14-digit with separators: XXXX[- /]XXXX[- /]XXXX[- /]XX (Diners Club style)
 *
 * WEB-193653: Added support for:
 *   - Forward slash (/) as separator
 *   - Diners Club 14-digit cards (36XX, 38XX, 300-305 prefixes)
 *   - Visa 19-digit extended format
 */
const creditCardPatterns: PatternDescriptor[] = [
  // Visa — 16-digit, leading 4, separators optional (space, hyphen, or slash)
  { source: '\\b4\\d{3}[\\s\\-/]?\\d{4}[\\s\\-/]?\\d{4}[\\s\\-/]?\\d{4}\\b', flags: 'g' },
  // Visa Extended — 19-digit, leading 4, separators optional (WEB-193653)
  { source: '\\b4\\d{3}[\\s\\-/]?\\d{4}[\\s\\-/]?\\d{4}[\\s\\-/]?\\d{4}[\\s\\-/]?\\d{3}\\b', flags: 'g' },
  // Mastercard — 51-55 or 2221-2720 prefix, 16 digits, separators optional
  {
    source:
      '\\b(?:5[1-5]\\d{2}|2(?:2[2-9]\\d|[3-6]\\d{2}|7[01]\\d|720))[\\s\\-/]?\\d{4}[\\s\\-/]?\\d{4}[\\s\\-/]?\\d{4}\\b',
    flags: 'g',
  },
  // Amex — 34/37 prefix, 15 digits (4-6-5), separators optional
  { source: '\\b3[47]\\d{2}[\\s\\-/]?\\d{6}[\\s\\-/]?\\d{5}\\b', flags: 'g' },
  // Diners Club — 36/38 prefix, 14 digits (4-4-4-2 or 4-6-4), separators optional (WEB-193653)
  { source: '\\b3[68]\\d{2}[\\s\\-/]?\\d{4}[\\s\\-/]?\\d{4}[\\s\\-/]?\\d{2}\\b', flags: 'g' },
  // Diners Club — 300-305 prefix, 14 digits, separators optional (WEB-193653)
  { source: '\\b30[0-5]\\d[\\s\\-/]?\\d{4}[\\s\\-/]?\\d{4}[\\s\\-/]?\\d{2}\\b', flags: 'g' },
  // Discover — 6011/644-649/65 prefix, 16 digits, separators optional
  { source: '\\b6(?:011|4[4-9]\\d|5\\d{2})[\\s\\-/]?\\d{4}[\\s\\-/]?\\d{4}[\\s\\-/]?\\d{4}\\b', flags: 'g' },
  // Generic 16-digit with mandatory separators (reduces false positives vs bare digit runs)
  { source: '\\b\\d{4}[\\s\\-/]\\d{4}[\\s\\-/]\\d{4}[\\s\\-/]\\d{4}\\b', flags: 'g' },
  // Generic 15-digit Amex-style with mandatory separators
  { source: '\\b\\d{4}[\\s\\-/]\\d{6}[\\s\\-/]\\d{5}\\b', flags: 'g' },
  // Generic 14-digit Diners-style with mandatory separators (WEB-193653)
  { source: '\\b\\d{4}[\\s\\-/]\\d{4}[\\s\\-/]\\d{4}[\\s\\-/]\\d{2}\\b', flags: 'g' },
];

// ==================== Email Pattern ====================
/**
 * Email address pattern following RFC 5322 simplified format.
 */
const emailPatterns: PatternDescriptor[] = [
  { source: '\\b[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\\.[a-zA-Z]{2,}\\b', flags: 'gi' },
];

// ==================== Phone Number Patterns ====================
/**
 * US phone number patterns.
 * Formats: (XXX) XXX-XXXX, XXX-XXX-XXXX, XXX.XXX.XXXX, +1 XXX XXX XXXX, etc.
 */
const usPhonePatterns: PatternDescriptor[] = [
  { source: '\\+1[-.\\s]?\\d{3}[-.\\s]?\\d{3}[-.\\s]?\\d{4}', flags: 'g' }, // With country code +1
  { source: '\\(\\d{3}\\)[-.\\s]?\\d{3}[-.\\s]?\\d{4}', flags: 'g' }, // Standard format with parentheses
  { source: '\\b\\d{3}[-.\\s]\\d{3}[-.\\s]\\d{4}\\b', flags: 'g' }, // Standard format without parentheses
];

/**
 * US phone number pattern for bare 10-digit numbers without separators.
 * Requires keyword proximity to reduce false positives (order numbers, confirmation codes, etc.)
 * WEB-193665: Phone number redaction fails when user enters US phone number without a space
 */
const usPhoneNoSeparatorPatterns: PatternDescriptor[] = [
  { source: '\\b\\d{10}\\b', flags: 'g' }, // Bare 10-digit number (requires keyword)
];
const usPhoneKeywords: string[] = [
  'phone',
  'call',
  'contact',
  'mobile',
  'cell',
  'tel',
  'telephone',
  'reach',
  'dial',
  'text',
  'sms',
  'whatsapp',
];

/**
 * International phone number patterns.
 * Excludes +1 (US/Canada) which is handled by US phone patterns.
 *
 * Two fixed-structure patterns replace the former single greedy pattern:
 *
 *   OLD (vulnerable): \+[2-9]\d{0,2}[-.\s]?\d{1,4}[-.\s]?\d{1,4}[-.\s]?\d{1,4}[-.\s]?\d{0,4}\b
 *   The chain of optional separators and variable-width digit groups created
 *   ambiguity that triggered catastrophic backtracking on crafted inputs
 *   (ReDoS / CWE-1333). Identified in ARB review by Staff Application Engineer.
 *
 * Fix: split into two patterns with mandatory separators between every digit
 * group so the engine never needs to backtrack across group boundaries:
 *   - Short:  +CC XXXXXXXX    (1-3 digit country code + 6-8 digit local number)
 *   - Grouped: +CC XXX-XXXX-XXXX  (1-3 digit country code + three hyphen/dot/space-delimited groups)
 *   - No separator: +CCXXXXXXXXXX (WEB-193665: country code directly followed by digits)
 */
const intlPhonePatterns: PatternDescriptor[] = [
  // International without separator: +XXXXXXXXXXXX — country code directly followed by digits
  // WEB-193665: Phone number redaction fails when user enters international phone number without a space
  { source: '\\+[2-9]\\d{9,12}\\b', flags: 'g' },
  // Short international: +XX XXXXXXXX — country code + one solid local block (6-10 digits for India, etc.)
  { source: '\\+[2-9]\\d{0,2}[-.\\s]\\d{6,10}\\b', flags: 'g' },
  // Long international with groups: +XX XXX-XXXX-XXXX
  { source: '\\+[2-9]\\d{0,2}[-.\\s]\\d{2,4}[-.\\s]\\d{3,4}[-.\\s]\\d{3,4}\\b', flags: 'g' },
];

// ==================== Passport Pattern ====================
/**
 * US Passport number pattern.
 * Format: 1 letter + 8 digits OR 9 digits
 * Requires keyword proximity for detection to reduce false positives.
 */
const passportPatterns: PatternDescriptor[] = [
  { source: '\\b[A-Z]\\d{8}\\b', flags: 'gi' },
  { source: '\\b\\d{9}\\b', flags: 'g' },
];
const passportKeywords: string[] = [
  'passport',
  'passeport',
  'travel document',
  'passport number',
  'passport no',
  'passport#',
  'passportno',
];

// ==================== Date of Birth Pattern ====================
/**
 * Date of birth patterns.
 * Formats: MM/DD/YYYY, MM-DD-YYYY, DD/MM/YYYY, YYYY-MM-DD
 * Requires keyword proximity for detection.
 */
const dobPatterns: PatternDescriptor[] = [
  { source: '\\b(0?[1-9]|1[0-2])[-/](0?[1-9]|[12]\\d|3[01])[-/](19|20)\\d{2}\\b', flags: 'g' }, // MM/DD/YYYY or MM-DD-YYYY
  { source: '\\b(0?[1-9]|[12]\\d|3[01])[-/](0?[1-9]|1[0-2])[-/](19|20)\\d{2}\\b', flags: 'g' }, // DD/MM/YYYY or DD-MM-YYYY
  { source: '\\b(19|20)\\d{2}[-/](0?[1-9]|1[0-2])[-/](0?[1-9]|[12]\\d|3[01])\\b', flags: 'g' }, // YYYY-MM-DD (ISO format)
];
const dobKeywords: string[] = [
  'dob',
  'd.o.b',
  'date of birth',
  'birth date',
  'birthday',
  'bday',
  'b-day',
  'born',
  'birthdate',
];

// ==================== Loyalty Number Pattern ====================
/**
 * Marriott Bonvoy loyalty number pattern.
 * Format: 9-12 digit number
 * Requires keyword proximity for detection.
 */
const loyaltyPatterns: PatternDescriptor[] = [{ source: '\\b\\d{9,12}\\b', flags: 'g' }];
const loyaltyKeywords: string[] = [
  'bonvoy',
  'marriott',
  'loyalty',
  'member',
  'rewards',
  'membership',
  'member number',
  'loyalty number',
  'bonvoy number',
  'marriott rewards',
];

// ==================== Driver's License Pattern ====================
/**
 * US Driver's License patterns (state-agnostic).
 * Includes both alphanumeric and pure-numeric formats.
 * Pure-numeric format requires keyword proximity to reduce false positives
 * with bank account and loyalty number patterns.
 * Requires keyword proximity for detection.
 */
const driversLicensePatterns: PatternDescriptor[] = [
  { source: '\\b[A-Z]\\d{7,12}\\b', flags: 'gi' }, // Alphanumeric format (many states)
  { source: '\\b\\d{7,13}\\b', flags: 'g' }, // Pure numeric format (requires keyword proximity)
];
const driversLicenseKeywords: string[] = [
  'driver license',
  "driver's license",
  'drivers license',
  'driving license',
  'dl',
  'dl#',
  'dlno',
  'driver licence',
  'drivers licence',
  "driver's licence",
  'license number',
  'licence number',
  'driver permit',
  'driving permit',
  'identification',
  'id card',
  'state id',
];

// ==================== Bank Account Pattern ====================
/**
 * Bank account number pattern.
 * Format: 8-17 digits
 * Requires keyword proximity for detection.
 */
const bankAccountPatterns: PatternDescriptor[] = [{ source: '\\b\\d{8,17}\\b', flags: 'g' }];
const bankAccountKeywords: string[] = [
  'account',
  'account number',
  'acct',
  'acct#',
  'bank account',
  'checking',
  'savings',
  'routing',
  'aba',
  'iban',
  'swift',
];

// ==================== ITIN Pattern ====================
/**
 * Individual Taxpayer Identification Number (ITIN) pattern.
 * Format: 9XX-XX-XXXX (starts with 9)
 */
const itinPatterns: PatternDescriptor[] = [
  { source: '\\b9\\d{2}[\\s-]?\\d{2}[\\s-]?\\d{4}\\b', flags: 'g' },
  { source: '\\b9\\d{8}\\b', flags: 'g' },
];

// ==================== Pattern Configurations ====================
/**
 * All pattern configurations for sensitive data detection.
 * Order matters: more specific patterns should come before generic ones.
 * Keyword-required patterns are processed first so they can take precedence
 * when their keyword is present.
 */
const patternConfigs: PatternConfig[] = [
  // Keyword-required patterns FIRST (more specific when keyword is present)
  {
    type: SensitiveDataType.PASSPORT_US,
    patterns: passportPatterns,
    redactor: (match: string) => {
      const chars = match.replace(/[^a-zA-Z0-9]/g, '');
      return `*****${chars.slice(-DIGITS_TO_SHOW)}`;
    },
    requiresKeyword: true,
    keywords: passportKeywords,
  },
  {
    type: SensitiveDataType.DATE_OF_BIRTH,
    patterns: dobPatterns,
    redactor: () => '**/**/****',
    requiresKeyword: true,
    keywords: dobKeywords,
  },
  {
    type: SensitiveDataType.LOYALTY_NUMBER,
    patterns: loyaltyPatterns,
    redactor: (match: string) => {
      const digits = match.replace(/\D/g, '');
      return `*****${digits.slice(-DIGITS_TO_SHOW)}`;
    },
    requiresKeyword: true,
    keywords: loyaltyKeywords,
  },
  {
    type: SensitiveDataType.DRIVERS_LICENSE,
    patterns: driversLicensePatterns,
    redactor: (match: string) => {
      const chars = match.replace(/[^a-zA-Z0-9]/g, '');
      return `***-**-${chars.slice(-DIGITS_TO_SHOW)}`;
    },
    requiresKeyword: true,
    keywords: driversLicenseKeywords,
  },
  {
    type: SensitiveDataType.BANK_ACCOUNT,
    patterns: bankAccountPatterns,
    redactor: (match: string) => {
      const digits = match.replace(/\D/g, '');
      return `****${digits.slice(-DIGITS_TO_SHOW)}`;
    },
    requiresKeyword: true,
    keywords: bankAccountKeywords,
  },

  // High confidence patterns (no keyword required)
  {
    type: SensitiveDataType.EMAIL,
    patterns: emailPatterns,
    redactor: () => '***@***.***',
    requiresKeyword: false,
    keywords: [],
  },
  {
    type: SensitiveDataType.CREDIT_CARD,
    patterns: creditCardPatterns,
    redactor: (match: string) => {
      const digits = match.replace(/\D/g, '');
      return `**** **** **** ${digits.slice(-DIGITS_TO_SHOW)}`;
    },
    requiresKeyword: false,
    keywords: [],
  },
  {
    type: SensitiveDataType.ITIN,
    patterns: itinPatterns,
    redactor: (match: string) => {
      const digits = match.replace(/\D/g, '');
      return `***-**-${digits.slice(-DIGITS_TO_SHOW)}`;
    },
    requiresKeyword: false,
    keywords: [],
  },
  {
    type: SensitiveDataType.SSN,
    patterns: ssnPatterns,
    redactor: (match: string) => {
      const digits = match.replace(/\D/g, '');
      return `***-**-${digits.slice(-DIGITS_TO_SHOW)}`;
    },
    requiresKeyword: false,
    keywords: [],
  },
  {
    type: SensitiveDataType.PHONE_INTL,
    patterns: intlPhonePatterns,
    redactor: (match: string) => {
      const digits = match.replace(/\D/g, '');
      return `+**-****-${digits.slice(-DIGITS_TO_SHOW)}`;
    },
    requiresKeyword: false,
    keywords: [],
  },
  {
    type: SensitiveDataType.PHONE_US,
    patterns: usPhonePatterns,
    redactor: (match: string) => {
      const digits = match.replace(/\D/g, '');
      return `(***) ***-${digits.slice(-DIGITS_TO_SHOW)}`;
    },
    requiresKeyword: false,
    keywords: [],
  },
  // WEB-193665: US phone without separators requires keyword to reduce false positives
  {
    type: SensitiveDataType.PHONE_US,
    patterns: usPhoneNoSeparatorPatterns,
    redactor: (match: string) => {
      const digits = match.replace(/\D/g, '');
      return `(***) ***-${digits.slice(-DIGITS_TO_SHOW)}`;
    },
    requiresKeyword: true,
    keywords: usPhoneKeywords,
  },
];

/**
 * Checks if any of the specified keywords are present near the match in the input text.
 * Keywords are searched within a proximity window of 50 characters before and after the match.
 *
 * @param input The full input text
 * @param matchStart The start index of the matched sensitive data
 * @param matchEnd The end index of the matched sensitive data
 * @param keywords List of keywords to search for
 * @returns True if any keyword is found in proximity
 */
function hasKeywordInProximity(input: string, matchStart: number, matchEnd: number, keywords: string[]): boolean {
  const startIndex = Math.max(0, matchStart - PROXIMITY_WINDOW);
  const endIndex = Math.min(input.length, matchEnd + PROXIMITY_WINDOW);
  const proximityText = input.substring(startIndex, endIndex).toLowerCase();

  return keywords.some(keyword => proximityText.includes(keyword.toLowerCase()));
}

/**
 * Represents a match found during pattern scanning.
 */
interface MatchInfo {
  start: number;
  end: number;
  value: string;
  config: PatternConfig;
}

/**
 * Redacts detected sensitive data from the input text.
 *
 * Each call constructs fresh RegExp instances from the stored pattern descriptors,
 * ensuring that the mutable `lastIndex` field is never shared across concurrent
 * callers (MOB_0001 v1.0.0 / MOB_0002 v1.0.0 / CWE-1333).
 *
 * Implements L3 Controls:
 * - MOB_0001 v1.0.0: Client-Side PII Detection and Redaction
 * - MOB_0002 v1.0.0: Sensitive Data Pattern Configuration (pattern processing order)
 *
 * Implements L2 Requirements:
 * - APP_0187 v1.0.0: "Utilize data masking or encryption techniques to protect sensitive information"
 * - BR_0007 v1.0.0: "Apply redaction rules for sensitive data types such as PII, PCI, or PHI"
 *
 * @param input The text to scan and redact
 * @returns RedactionResult containing the redacted text, detection flag, and types found
 */
export function redact(input: string): RedactionResult {
  // Handle null, undefined, or empty input
  if (!input || typeof input !== 'string' || input.length === 0) {
    return {
      redactedText: input ?? '',
      containsSensitiveData: false,
      detectedTypes: new Set<SensitiveDataType>(),
    };
  }

  const detectedTypes = new Set<SensitiveDataType>();
  const allMatches: MatchInfo[] = [];

  // Collect all matches from all patterns.
  // A fresh RegExp is constructed from each descriptor so lastIndex is always 0
  // and no state leaks between calls (thread-safe, safe under concurrent renders).
  for (const config of patternConfigs) {
    for (const descriptor of config.patterns) {
      const pattern = new RegExp(descriptor.source, descriptor.flags);

      let match: RegExpExecArray | null;
      while ((match = pattern.exec(input)) !== null) {
        const shouldRedact =
          !config.requiresKeyword ||
          hasKeywordInProximity(input, match.index, match.index + match[0].length, config.keywords);

        if (shouldRedact) {
          allMatches.push({
            start: match.index,
            end: match.index + match[0].length,
            value: match[0],
            config,
          });
        }
      }
    }
  }

  // Sort matches by start position (ascending), then by length (descending) for same start
  // This ensures longer matches take precedence over shorter overlapping ones
  allMatches.sort((a, b) => {
    if (a.start !== b.start) {
      return a.start - b.start;
    }
    return b.value.length - a.value.length;
  });

  // Remove overlapping matches, preferring earlier/longer matches
  const nonOverlappingMatches: MatchInfo[] = [];
  for (const match of allMatches) {
    const hasOverlap = nonOverlappingMatches.some(
      existing =>
        (match.start >= existing.start && match.start < existing.end) ||
        (match.end > existing.start && match.end <= existing.end) ||
        (match.start <= existing.start && match.end >= existing.end)
    );

    if (!hasOverlap) {
      nonOverlappingMatches.push(match);
    }
  }

  // Sort by position descending for replacement (to avoid index shifting)
  nonOverlappingMatches.sort((a, b) => b.start - a.start);

  // Apply redactions
  let redactedText = input;
  for (const match of nonOverlappingMatches) {
    detectedTypes.add(match.config.type);
    const redactedValue = match.config.redactor(match.value);
    redactedText = redactedText.substring(0, match.start) + redactedValue + redactedText.substring(match.end);
  }

  return {
    redactedText,
    containsSensitiveData: detectedTypes.size > 0,
    detectedTypes,
  };
}

/**
 * Gets a human-readable label for a sensitive data type.
 *
 * @param type The sensitive data type
 * @returns A human-readable label
 */
export function getSensitiveDataTypeLabel(type: SensitiveDataType): string {
  const labels: Record<SensitiveDataType, string> = {
    [SensitiveDataType.SSN]: 'Social Security Number',
    [SensitiveDataType.CREDIT_CARD]: 'Credit Card',
    [SensitiveDataType.EMAIL]: 'Email',
    [SensitiveDataType.PHONE_US]: 'Phone Number',
    [SensitiveDataType.PHONE_INTL]: 'International Phone',
    [SensitiveDataType.PASSPORT_US]: 'Passport',
    [SensitiveDataType.DATE_OF_BIRTH]: 'Date of Birth',
    [SensitiveDataType.LOYALTY_NUMBER]: 'Loyalty Number',
    [SensitiveDataType.DRIVERS_LICENSE]: "Driver's License",
    [SensitiveDataType.BANK_ACCOUNT]: 'Bank Account',
    [SensitiveDataType.ITIN]: 'ITIN',
  };
  return labels[type] || type;
}

/**
 * Formats detected types into a comma-separated string for display.
 *
 * @param detectedTypes Set of detected sensitive data types
 * @returns Comma-separated string of type labels
 */
export function formatDetectedTypes(detectedTypes: Set<SensitiveDataType> | null | undefined): string {
  if (!detectedTypes || detectedTypes.size === 0) {
    return '';
  }
  return Array.from(detectedTypes).map(getSensitiveDataTypeLabel).join(', ');
}
